import { getFreshToken, stepUp } from '../auth/keycloak'
import { runtimeConfig } from '../runtimeConfig'
import type { ProblemDetails } from './types'

export const CORRELATION_HEADER = 'X-Correlation-Id'

/**
 * Resolves an API path against the configured origin. Read per call from the runtime
 * configuration rather than captured at module load, because it arrives from
 * /config.json at startup.
 */
export function apiUrl(path: string): string {
  const base = runtimeConfig().apiBaseUrl
  return base ? `${base}${path}` : path
}

/** An API failure carrying the server's Problem Details and the request's correlation id. */
export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetails
  readonly correlationId: string | null

  constructor(status: number, problem: ProblemDetails, correlationId: string | null) {
    super(problem.detail ?? problem.title ?? `Request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
    this.correlationId = correlationId
  }

  /** Field-level violations, keyed by camelCase field name, so a form can show them inline. */
  get violations(): Record<string, string> {
    const map: Record<string, string> = {}
    for (const v of this.problem.violations ?? []) {
      map[v.field] = v.message
    }
    return map
  }
}

/** A successful response together with the correlation id the API stamped on it. */
export interface ApiResponse<T> {
  data: T
  correlationId: string | null
}

/**
 * A W3C `traceparent` for one request: `00-<trace-id>-<parent-id>-01`.
 *
 * ASP.NET Core reads it into Activity.Current, and the API's correlation id IS the
 * trace id -- so the id the browser generated is the one on the log line, the Problem
 * Details body and every audit row. Random per request; there is no browser-side span.
 */
export function newTraceparent(random: (bytes: Uint8Array) => Uint8Array = fill): string {
  const traceId = hex(random(new Uint8Array(16)))
  const parentId = hex(random(new Uint8Array(8)))
  return `00-${traceId}-${parentId}-01`
}

function fill(bytes: Uint8Array): Uint8Array {
  crypto.getRandomValues(bytes)
  return bytes
}

function hex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

async function request<T>(path: string, init?: RequestInit): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    traceparent: newTraceparent(),
    ...(init?.body === undefined || init.body instanceof Blob
      ? {}
      : { 'Content-Type': 'application/json' }),
    ...(init?.headers as Record<string, string> | undefined),
  }

  const token = await getFreshToken(30)
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const response = await fetch(apiUrl(path), { ...init, headers })
  const correlationId = response.headers.get(CORRELATION_HEADER)

  if (response.status === 204) {
    return { data: undefined as T, correlationId }
  }

  const text = await response.text()
  let payload: unknown = undefined
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = { title: text }
    }
  }

  if (!response.ok) {
    const problem = (payload ?? {}) as ProblemDetails
    if (isStepUp(response.status, problem)) stepUp()
    throw new ApiError(response.status, problem, correlationId)
  }

  return { data: payload as T, correlationId }
}

/**
 * The API's step-up challenge (ADR 0011): 401 with `acrValues` in the body. Read from the
 * body, not the WWW-Authenticate header, which a cross-origin script cannot see.
 */
export function isStepUp(status: number, problem: ProblemDetails): boolean {
  return status === 401 && typeof problem.acrValues === 'string'
}

export const api = {
  get: <T>(path: string) => request<T>(path),

  /**
   * Sends a File (or Blob) as the RAW request body. No multipart: the API streams the
   * body straight to quarantine, hashing it on the way, so the bytes are never
   * buffered on either side.
   */
  upload: <T>(path: string, file: Blob, contentType?: string) =>
    request<T>(path, {
      method: 'POST',
      body: file,
      headers: { 'Content-Type': contentType ?? (file.type || 'application/octet-stream') },
    }),

  /** Fetches bytes with the bearer token and returns them as a Blob, with the file name the API chose. */
  download: async (
    path: string,
  ): Promise<{
    blob: Blob
    fileName: string | null
    correlationId: string | null
    headers: Headers
  }> => {
    const headers: Record<string, string> = { traceparent: newTraceparent() }
    const token = await getFreshToken(30)
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
    const response = await fetch(apiUrl(path), { headers })
    const correlationId = response.headers.get(CORRELATION_HEADER)
    if (!response.ok) {
      let payload: ProblemDetails = {}
      try {
        payload = (await response.json()) as ProblemDetails
      } catch {
        /* no body */
      }
      if (isStepUp(response.status, payload)) stepUp()
      throw new ApiError(response.status, payload, correlationId)
    }
    const disposition = response.headers.get('Content-Disposition') ?? ''
    const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
    const plain = /filename="?([^";]+)"?/i.exec(disposition)
    const fileName = star?.[1] ? decodeURIComponent(star[1]) : (plain?.[1] ?? null)
    return { blob: await response.blob(), fileName, correlationId, headers: response.headers }
  },

  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),

  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),

  del: (path: string) => request<void>(path, { method: 'DELETE' }),

  /** A DELETE whose reply carries a body (the room after its NDA was removed). */
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
